import { CheckCircleOutlined } from "@ant-design/icons";
import { Modal, Form, Input, Select, Switch } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

const DEFAULT_AUDIENCE_PROFILE = "other";

const UserFormModal = ({
                           open,
                           onClose,
                           onSubmit,
                           initialValues,
                           clientOptions,
                           currentUser,
                       }) => {
    const { t } = useTranslation("users");
    const [form] = Form.useForm();
    const activeValue = Form.useWatch("is_active", form);

    const isEditing = !!initialValues;
    const isSuperAdmin = currentUser?.role === "SUPER_ADMIN";

    const isSelfEdit = isEditing && currentUser?.id && initialValues?.id === currentUser.id;
    const isCreate = !initialValues;

    // Seul super admin (pas self) peut toucher role/client.
    const canEditSensitive = isSuperAdmin && !isSelfEdit;

    const roleOptions = useMemo(() => {
        if (!isSuperAdmin) {
            return [{ value: "USER", label: t("roles.USER") }];
        }
        return [
            { value: "SUPER_ADMIN", label: t("roles.SUPER_ADMIN") },
            { value: "ADMIN_CLIENT", label: t("roles.ADMIN_CLIENT") },
            { value: "USER", label: t("roles.USER") },
        ];
    }, [isSuperAdmin, t]);

    const audienceProfileOptions = useMemo(() => [
        { value: "legal_professional", label: t("audienceProfiles.legal_professional") },
        { value: "entrepreneur_executive", label: t("audienceProfiles.entrepreneur_executive") },
        { value: "sme_operations", label: t("audienceProfiles.sme_operations") },
        { value: "other", label: t("audienceProfiles.other") },
    ], [t]);

    const sortedClientOptions = useMemo(() => {
        return [...(clientOptions || [])]
            .filter((client) => client && client.id)
            .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }));
    }, [clientOptions]);

    const optionContainsText = (input, option) => {
        return String(option?.label || "")
            .toLowerCase()
            .includes((input || "").toLowerCase());
    };

    useEffect(() => {
        if (!open) return;

        if (initialValues) {
            form.setFieldsValue({
                ...initialValues,
                audience_profile: initialValues.audience_profile ?? DEFAULT_AUDIENCE_PROFILE,
                is_active: initialValues.is_active ?? true,
            });
            return;
        }

        // Create mode
        form.resetFields();

        if (!isSuperAdmin) {
            // Non-super: defaults verrouillés
            form.setFieldsValue({
                role: "USER",
                client_id: currentUser?.client_id ?? undefined,
                is_active: true,
            });
        } else {
            // Super admin: libre, mais active par défaut
            form.setFieldsValue({ is_active: true });
        }
    }, [open, initialValues, form, isSuperAdmin, currentUser?.client_id]);

    const sanitizeValues = (values) => {
        const cleaned = { ...values };
        delete cleaned.max_token;

        // Non-super: force sécurité
        if (!isSuperAdmin) {
            cleaned.role = "USER";
            cleaned.client_id = currentUser?.client_id ?? cleaned.client_id;
        }

        // Self-edit: pas de champs sensibles
        if (isSelfEdit) {
            delete cleaned.role;
            delete cleaned.client_id;
        }


        return cleaned;
    };

    const handleOk = () => {
        form
            .validateFields()
            .then((values) => {
                onSubmit(sanitizeValues(values));
            })
            .catch(() => {});
    };

    const toggleActive = () => {
        form.setFieldsValue({ is_active: !(activeValue ?? true) });
    };

    return (
        <Modal
            title={initialValues ? t("form.editTitle") : t("form.addTitle")}
            open={open}
            onCancel={onClose}
            onOk={handleOk}
            centered
            destroyOnHidden
            okText={initialValues ? t("form.update") : t("form.create")}
            okButtonProps={{ className: "verdictia-primary-btn" }}
            className="user-form-modal"
            width={740}
        >
            <Form layout="vertical" form={form} initialValues={{ is_active: true }}>
                <div className="user-form-grid">
                    <Form.Item
                        name="name"
                        label={t("form.firstname")}
                        rules={[{ required: true, message: t("form.required") }]}
                    >
                        <Input placeholder={t("form.firstnamePlaceholder")} />
                    </Form.Item>

                    <Form.Item
                        name="surname"
                        label={t("form.lastname")}
                        rules={[{ required: true, message: t("form.required") }]}
                    >
                        <Input placeholder={t("form.lastnamePlaceholder")} />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label={t("form.email")}
                        rules={[
                            { required: true, message: t("form.required") },
                            { type: "email", message: t("form.invalidEmail") },
                        ]}
                    >
                        <Input placeholder={t("form.emailPlaceholder")} />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label={t("form.password")}
                        rules={!initialValues ? [{ required: true, message: t("form.required") }] : []}
                    >
                        <Input.Password placeholder={t("form.passwordPlaceholder")} />
                    </Form.Item>

                    <Form.Item
                        name="role"
                        label={t("form.role")}
                        rules={[{ required: true, message: t("form.required") }]}
                    >
                        <Select
                            placeholder={t("form.rolePlaceholder")}
                            showSearch
                            optionFilterProp="label"
                            filterOption={optionContainsText}
                            disabled={isCreate ? false : !canEditSensitive}
                            options={roleOptions}
                        />
                    </Form.Item>

                    {isSuperAdmin ? (
                        <Form.Item
                            name="client_id"
                            label={t("form.client")}
                            rules={isSuperAdmin ? [{ required: true, message: t("form.required") }] : []}
                        >
                            <Select
                                placeholder={t("form.clientPlaceholder")}
                                showSearch
                                optionFilterProp="label"
                                filterOption={optionContainsText}
                                disabled={!canEditSensitive}
                                options={sortedClientOptions.map((client) => ({
                                    value: client.id,
                                    label: client.name,
                                }))}
                            />
                        </Form.Item>
                    ) : (
                        <Form.Item name="client_id" hidden>
                            <Input />
                        </Form.Item>
                    )}

                    <Form.Item
                        className="user-form-field--full user-form-field--audience"
                        name="audience_profile"
                        label={t("form.audienceProfile")}
                        extra={t("form.audienceProfileHelp")}
                        rules={[{ required: true, message: t("form.required") }]}
                    >
                        <Select
                            data-testid="audience-profile-select"
                            placeholder={t("form.audienceProfilePlaceholder")}
                            showSearch
                            optionFilterProp="label"
                            filterOption={optionContainsText}
                            options={audienceProfileOptions}
                        />
                    </Form.Item>

                    <Form.Item className="user-form-field--state">
                        <div
                            role="button"
                            tabIndex={0}
                            className={`user-form-state-card ${(activeValue ?? true) ? "user-form-state-card--active" : ""}`}
                            onClick={toggleActive}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    toggleActive();
                                }
                            }}
                        >
                            <span className="user-form-state-card__icon" aria-hidden="true">
                                <CheckCircleOutlined />
                            </span>
                            <span className="user-form-state-card__content">
                                <span>{t("form.active")}</span>
                                <small>
                                    {(activeValue ?? true) ? t("status.active") : t("status.inactive")}
                                </small>
                            </span>
                            <span onClick={(event) => event.stopPropagation()}>
                                <Form.Item name="is_active" valuePropName="checked" noStyle>
                                    <Switch
                                        checked={activeValue ?? true}
                                        onChange={(checked) => form.setFieldsValue({ is_active: checked })}
                                        size="small"
                                    />
                                </Form.Item>
                            </span>
                        </div>
                    </Form.Item>
                </div>
            </Form>
        </Modal>
    );
};

export default UserFormModal;
